import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { collection, query, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import type { MatchDocument } from './types';
import { MatchPhase } from './types';

interface MatchWithNotification extends MatchDocument {
  hasNotification: boolean;
}

function Matches(): React.ReactElement {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<MatchWithNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatches = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      const matchesRef = collection(db, 'users', user.uid, 'matches');
      const matchesQuery = query(matchesRef, orderBy('createdAt', 'desc'));
      const matchesSnapshot = await getDocs(matchesQuery);

      const matchesList: MatchWithNotification[] = [];

      for (const matchDocSnap of matchesSnapshot.docs) {
        const data = matchDocSnap.data();

        const match: MatchDocument = {
          matchedUserId: data.matchedUserId,
          matchedUserName: data.matchedUserName,
          createdAt: data.createdAt,
          outdated: data.outdated || false,
          phase: data.phase || MatchPhase.QuestionsOnly,
          beaconShareRequested: data.beaconShareRequested || false,
          flips: data.flips
        };

        let hasNotification = false;
        if (match.phase < MatchPhase.BeaconsUnlocked && !match.beaconShareRequested) {
          try {
            const reverseMatchRef = doc(db, 'users', match.matchedUserId, 'matches', user.uid);
            const reverseMatchSnap = await getDoc(reverseMatchRef);
            if (reverseMatchSnap.exists()) {
              const reverseData = reverseMatchSnap.data();
              hasNotification = reverseData.beaconShareRequested === true;
            }
          } catch (error) {
            console.error('Error reading reverse match:', error);
          }
        }

        matchesList.push({ ...match, hasNotification });
      }

      setMatches(matchesList);
      setLoading(false);
    };

    fetchMatches();
  }, []);

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        Back to Profile
      </button>

      <h1>Your Matches</h1>

      {loading ? (
        <p>Loading matches...</p>
      ) : matches.length === 0 ? (
        <p>No matches yet. Keep answering questions to find compatible people!</p>
      ) : (
        <div>
          <p>You have {matches.length} match{matches.length !== 1 ? 'es' : ''}:</p>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {matches.map((match) => (
              <li
                key={match.matchedUserId}
                onClick={() => navigate(`/matches/${match.matchedUserId}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = match.outdated ? '#ffe69c' : '#e9ecef';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = match.outdated ? '#fff3cd' : '#f8f9fa';
                }}
                style={{
                  padding: '15px',
                  marginBottom: '10px',
                  backgroundColor: match.outdated ? '#fff3cd' : '#f8f9fa',
                  borderRadius: '8px',
                  border: match.outdated ? '1px solid #ffc107' : '1px solid #dee2e6',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{match.matchedUserName}</strong>
                    {match.hasNotification && (
                      <span
                        style={{
                          marginLeft: '8px',
                          backgroundColor: '#2196F3',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        NEW REQUEST
                      </span>
                    )}
                    <br />
                    <small style={{ color: '#6c757d' }}>
                      Matched on {match.createdAt.toDate().toLocaleDateString()}
                      {match.outdated && <span style={{ color: '#856404', marginLeft: '8px' }}>(Outdated)</span>}
                    </small>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Matches;
